import { useTranslation } from 'react-i18next';
import SheetShell from '../SheetShell';
import styles from './ChoiceSheet.module.css';

interface ChoiceSheetProps {
  onClose: () => void;
  onSendAsText: () => void;
  onMailACard: () => void;
  /** Coordinator z for the share-choice / detail-share-choice layer (A1). */
  zIndex?: number;
}

/**
 * Choice sheet between Send-a-card entry points and the destination
 * sheet. Mounted only when VITE_PICKER_ENABLED is true. The two
 * destinations are the free image-share path (LetThemKnowSheet) and
 * the printed-card flow (MailCardSheet, Phase 2A; replaces the prior
 * Picker mount per docs/plans/send-a-card-phase-2a-foundation-2026-05-27.md
 * § 5.3). When the flag is off, both entry points bypass this sheet
 * and route directly to LetThemKnowSheet so the pre-launch posture is
 * preserved.
 *
 * Visual grammar mirrors LeafActionSheet's cardPrimary rows so the
 * surfaces feel like one family. SheetShell owns the backdrop, the
 * slide-up animation, and visualViewport keyboard tracking; ChoiceSheet
 * owns only the title plus the two card buttons.
 */
export default function ChoiceSheet({
  onClose,
  onSendAsText,
  onMailACard,
  zIndex,
}: ChoiceSheetProps) {
  const { t } = useTranslation();

  return (
    <SheetShell
      ariaLabel={t('sendChoice.title')}
      onBackdrop={onClose}
      closeLabel={t('sendChoice.closeAria')}
      zIndex={zIndex}
      dragToDismiss
    >
      <h2 className={styles.sheetTitle}>{t('sendChoice.title')}</h2>
      <ul className={styles.cardList}>
        <li>
          <button
            type="button"
            className={styles.card}
            onClick={onSendAsText}
          >
            <span className={styles.cardBody}>
              <span className={styles.cardTitle}>
                {t('sendChoice.sendAsTextLabel')}
              </span>
              <span className={styles.cardHelper}>
                {t('sendChoice.sendAsTextHelper')}
              </span>
            </span>
          </button>
        </li>
        <li>
          <button
            type="button"
            className={styles.card}
            onClick={onMailACard}
          >
            <span className={styles.cardBody}>
              <span className={styles.cardTitle}>
                {t('sendChoice.mailACardLabel')}
              </span>
              <span className={styles.cardHelper}>
                {t('sendChoice.mailACardHelper')}
              </span>
            </span>
          </button>
        </li>
      </ul>
    </SheetShell>
  );
}
